import { computed, ref } from 'vue';

import { DateRangeMode } from '../models/entities/calendar.model';

import { SlideInformation } from '../models/entities/text-carousel.model';

import CalendarService from '../services/calendar.service';
import DateService from '../services/date.service';
import UtilsService from '../services/utils.service';

import { useStore } from './store.composable';
import { useMediaQuery } from './media-query.composable';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useDatePicker = (props, { emit, slots }) => {
  const { state } = useStore();
  const { browserDetect } = useMediaQuery();
  // ----------------------------------------
  // variables
  const showDatePicker = ref(false);

  const inputControl = ref();

  // ----------------------------------------
  // computed
  const datePickerVisibility = computed((): boolean => {
    return props.pickerVisibility ?? showDatePicker.value;
  });

  const getAvailableDates = computed((): string[] => {
    return props.dealerCalendar.availableDates;
  });

  const inputFormattedDate = computed((): string => {
    if (!state.otbcFlow.selectedDay) {
      return 'dd/mm/yyyy';
    }
    const dates = !props.rangePicker.enabled
      ? ({ start: props.selectedDay } as DateRangeMode)
      : props.selectedDates;
    const dateValue = CalendarService.isStartMode(props.rangePicker.workingMode)
      ? dates.start
      : dates.end;
    return DateService.formatDateForVBS(dateValue);
  });

  const selectedDatesValue = computed((): DateRangeMode => {
    return !props.rangePicker.enabled
      ? ({ start: props.selectedDay } as DateRangeMode)
      : props.selectedDates;
  });

  // ----------------------------------------
  // methods
  const addEventListeners = (elements: Element[]): void => {
    elements.forEach((element) => {
      element.addEventListener('click', toggle);
      if (browserDetect() === 'safari') {
        element.addEventListener('touchend', toggle);
      }
    });
  };

  const changeDate = (date: Date): void => {
    const isInvalidDate = !getAvailableDates?.value.includes(
      DateService.formatDateForVBS(date)
    );
    const isWrongFormatDate = date && !UtilsService.isDateValid(date);

    if (isWrongFormatDate || isInvalidDate) {
      if (props.isOptional) {
        // Propagate the event
        emit('changeDate', date);
      }
      return;
    }

    emit('changeDate', date);
  };

  const changeSheet = (page: SlideInformation): void => {
    maskCalendarSection();
    emit('changeSheet', page);
    unmaskCalendarSection();
  };

  const clickableElementsObserver = (): void => {
    new MutationObserver((_, observer) => {
      // Choosed to do a querySelector since is way faster then iterate for all the list
      const clickableElements = getClickableElements();
      if (clickableElements) {
        addEventListeners(clickableElements);
        observer.disconnect();
      }
    }).observe(inputControl.value, {
      childList: true,
      subtree: true,
    });
  };

  const closePickerCalendar = (): void => {
    togglePickerCalendar(false);
  };

  const getClickableElements = (): HTMLElement[] | undefined | null => {
    return inputControl.value?.querySelectorAll(
      '.otb-date-picker__input, wb-icon[name*="calendar"]'
    );
  };

  const inputDateWithDebounce = UtilsService.debounce((event: Event): void => {
    const dateInput = (event.target as HTMLInputElement).value;
    if (dateInput) {
      const newDate = new Date(dateInput);
      const now = new Date();
      newDate.setHours(now.getHours(), now.getMinutes());
      changeDate(newDate);
    }
  }, 500);

  const maskCalendarSection = (): void => {
    emit('maskCalendar');
  };

  const removeEventListeners = (): void => {
    const clickableElements = getClickableElements();
    if (clickableElements) {
      clickableElements?.forEach((element) => {
        element.removeEventListener('click', toggle);
      });
    }
  };

  const supportContentFilled = (): string => {
    return slots.supportContent ? 'otb-date-picker-input-group__extended' : '';
  };

  const toggle = (event): void => {
    event.preventDefault(true);
    if (!(event.type == 'touchend' && !event.cancelable)) {
      togglePickerCalendar();
    }
  };

  const togglePickerCalendar = (forceStatus?: boolean): void => {
    if (forceStatus === datePickerVisibility.value) return;
    showDatePicker.value = forceStatus ?? !datePickerVisibility.value;
    if (props.rangePicker.enabled) {
      emit('onPickerToggle', showDatePicker.value);
    }
  };

  const unmaskCalendarSection = (): void => {
    emit('unmaskCalendar');
  };

  return {
    addEventListeners,
    changeDate,
    changeSheet,
    clickableElementsObserver,
    closePickerCalendar,
    datePickerVisibility,
    getAvailableDates,
    getClickableElements,
    inputControl,
    inputDateWithDebounce,
    inputFormattedDate,
    maskCalendarSection,
    removeEventListeners,
    selectedDatesValue,
    showDatePicker,
    supportContentFilled,
    toggle,
    togglePickerCalendar,
    unmaskCalendarSection,
  };
};

export default useDatePicker;
